import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import AnalyticsService from "services/AnalyticsService";
import OrganisationService from "services/OrganisationService";
import useMutation from "hooks/useMutation";
import useSubscription from "hooks/useSubscription";
import type { UpsellFeature } from "types/upsell";

const LEGACY_PLAN_TYPE = "Legacy";

interface UseUpsellRequestAccessFetcher {
  route: string;
}

/**
 * Sends a plan-upgrade request to the support team for the "Request access" CTA.
 *
 * The API derives who is asking, their role, their organisation and their plan
 * from the auth token, so the only thing we supply is the route the request came
 * from. Responds `204`, so there is nothing to read back — success is simply the
 * absence of an error.
 */
function useUpsellRequestAccess(feature: UpsellFeature, origin: string) {
  const { pathname } = useLocation();
  const subscription = useSubscription();
  /** Legacy orgs have no subscription; report them as a tier rather than dropping them. */
  const planType = subscription?.planType ?? LEGACY_PLAN_TYPE;

  const cacheKey = useMemo(() => ["OrganisationService.requestUpgrade"] as const, []);

  const mutation = useMutation<void, Error, typeof cacheKey, UseUpsellRequestAccessFetcher>(
    cacheKey,
    async (_, { arg }) => {
      await OrganisationService.requestUpgrade(arg.route);
    }
  );

  const { trigger } = mutation;

  /**
   * Keyed on the reported values rather than the whole subscription, so the
   * handler identity only changes when something it actually sends changes.
   */
  const requestAccess = useCallback(async () => {
    AnalyticsService.logEvent(AnalyticsService.EVENTS.UPSELL_REQUEST_ACCESS_CLICKED, {
      [AnalyticsService.PROPERTIES.UPSELL_FEATURE]: feature,
      [AnalyticsService.PROPERTIES.UPSELL_PLAN_TYPE]: planType,
      [AnalyticsService.PROPERTIES.ORIGIN]: origin,
    });

    await trigger({ route: pathname });
  }, [feature, origin, planType, pathname, trigger]);

  return useMemo(
    () => ({
      requestAccess,
      isRequesting: mutation.isMutating,
      isError: Boolean(mutation.error),
    }),
    [requestAccess, mutation.isMutating, mutation.error]
  );
}

export default useUpsellRequestAccess;
